import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { GripVertical, Eye, EyeOff } from 'lucide-react'

interface ColumnConfig {
  id: string
  label: string
  visible: boolean
  order: number
  pinned: boolean
}

interface LinkedInColumnManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onColumnsUpdated: (columns: ColumnConfig[]) => void
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'sender_name', label: 'Sender Name', visible: true, order: 0, pinned: false },
  { id: 'linkedin_url', label: 'LinkedIn URL', visible: true, order: 1, pinned: false },
  { id: 'client_name', label: 'Client', visible: true, order: 2, pinned: false },
  { id: 'daily_connections', label: 'Daily Connections', visible: true, order: 3, pinned: false },
  { id: 'daily_inmails', label: 'Daily InMails', visible: true, order: 4, pinned: false },
  { id: 'daily_direct_messages', label: 'Daily Messages', visible: true, order: 5, pinned: false },
  { id: 'connections_accepted', label: 'Connections Accepted', visible: true, order: 6, pinned: false },
  { id: 'direct_messages_sent', label: 'Messages Sent', visible: true, order: 7, pinned: false },
  { id: 'inmails_sent', label: 'InMails Sent', visible: true, order: 8, pinned: false },
  { id: 'sync_status', label: 'Sync Status', visible: true, order: 9, pinned: false },
  { id: 'platform_status', label: 'Platform Status', visible: false, order: 10, pinned: false },
  { id: 'last_synced_at', label: 'Last Synced', visible: false, order: 11, pinned: false },
  { id: 'needs_sync', label: 'Needs Sync', visible: false, order: 12, pinned: false },
  { id: 'heyreach_id', label: 'Heyreach ID', visible: false, order: 13, pinned: false }
]

export function LinkedInColumnManagerDialog({ open, onOpenChange, onColumnsUpdated }: LinkedInColumnManagerDialogProps) {
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS)
  const [customLabels, setCustomLabels] = useState<Record<string, string>>({})

  // Load saved configuration on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('linkedin-column-config')
    const savedLabels = localStorage.getItem('linkedin-column-labels')
    
    if (savedConfig) {
      try {
        setColumns(JSON.parse(savedConfig))
      } catch (error) {
        console.error('Failed to parse saved column config:', error)
      }
    }
    
    if (savedLabels) {
      try {
        setCustomLabels(JSON.parse(savedLabels))
      } catch (error) {
        console.error('Failed to parse saved column labels:', error)
      }
    }
  }, [])

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible } : col
    ))
  }

  const handleColumnLabelChange = (columnId: string, newLabel: string) => {
    setCustomLabels(prev => ({
      ...prev,
      [columnId]: newLabel
    }))
    
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, label: newLabel } : col
    ))
  }

  const handleDragEnd = (result: any) => {
    if (!result.destination) return

    const items = Array.from(columns)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update order property
    const updatedColumns = items.map((col, index) => ({
      ...col,
      order: index
    }))

    setColumns(updatedColumns)
  }

  const handleSave = () => {
    // Save to localStorage
    localStorage.setItem('linkedin-column-config', JSON.stringify(columns))
    localStorage.setItem('linkedin-column-labels', JSON.stringify(customLabels))
    
    // Notify parent component
    onColumnsUpdated(columns)
    onOpenChange(false)
  }

  const handleReset = () => {
    setColumns(DEFAULT_COLUMNS)
    setCustomLabels({})
    localStorage.removeItem('linkedin-column-config')
    localStorage.removeItem('linkedin-column-labels')
  }

  const visibleCount = columns.filter(col => col.visible).length
  const totalCount = columns.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Manage LinkedIn Profile Columns</DialogTitle>
          <div className="text-sm text-muted-foreground">
            Customize which columns are visible and their order. Drag to reorder.
          </div>
        </DialogHeader>
        
        <div className="flex items-center justify-between py-4 border-b">
          <div className="flex items-center gap-4">
            <Badge variant="outline">
              {visibleCount} of {totalCount} columns visible
            </Badge>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset}>
            Reset to Default
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto max-h-[400px]">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="columns">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {columns.map((column, index) => (
                    <Draggable
                      key={column.id}
                      draggableId={column.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-3 p-3 border rounded-lg bg-background ${
                            snapshot.isDragging ? 'shadow-lg' : ''
                          }`}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing"
                          >
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={column.visible}
                              onCheckedChange={(checked) =>
                                handleColumnVisibilityChange(column.id, checked)
                              }
                            />
                            {column.visible ? (
                              <Eye className="h-4 w-4 text-green-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>

                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-muted-foreground">
                                Column ID
                              </Label>
                              <div className="font-mono text-sm">{column.id}</div>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">
                                Display Label
                              </Label>
                              <Input
                                value={customLabels[column.id] || column.label}
                                onChange={(e) =>
                                  handleColumnLabelChange(column.id, e.target.value)
                                }
                                className="h-8"
                                placeholder="Enter display label"
                              />
                            </div>
                          </div>

                          {column.pinned && (
                            <Badge variant="secondary" className="text-xs">
                              Pinned
                            </Badge>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}